import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { handleSignOut } from '../../utils/auth';

const UserProfile = ({ 
  userInfo, 
  isLoading, 
  isDarkMode, 
  isMobile, 
  isCollapsed,
  handleMobileItemClick,
  navigate,
  role 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const initials = (() => {
    if (!userInfo.name || userInfo.name === 'Account User') {
      return 'AU';
    }
    
    const nameParts = userInfo.name.split(" ").filter(part => 
      part && 
      part.length > 0 && 
      !/[0-9]/.test(part)
    );
    
    if (nameParts.length === 0) return 'AU';
    
    return nameParts
      .map(n => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  })();

  if (isLoading) {
    return (
      <div className={`flex ${isMobile ? "px-4 py-3" : ""} items-center justify-center`}>
        <div className="w-8 h-8 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-2 mt-2">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="relative w-10 h-10 rounded-full flex items-center justify-center bg-brand-logo-purple/20 text-brand-logo-purple border border-brand-logo-purple/30">
            {userInfo.image ? (
              <img
                src={userInfo.image}
                alt={userInfo.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <span className="text-sm font-semibold">{initials}</span>
            )}
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-brand-void-black"></div>
          </div>
          <div className="flex-1">
            <div className="font-medium">{userInfo.name}</div>
            <div className="text-xs text-gray-500">{userInfo.email}</div>
          </div>
        </div>
        
        <div className="flex flex-col px-3 space-y-1">
          <Link
            to={`/${role}/profile`}
            className={`flex items-center gap-2 py-2 px-4 rounded-lg ${
              isDarkMode
                ? "hover:bg-brand-logo-purple/10 text-gray-300 hover:text-brand-logo-purple"
                : "hover:bg-gray-100 text-gray-700"
            }`}
            onClick={handleMobileItemClick}
          >
            <User size={18} />
            <span>My Profile</span>
          </Link>
          
          <button
            onClick={() => {
              handleMobileItemClick();
              handleSignOut(navigate);
            }}
            className={`w-full flex items-center gap-2 py-2 px-4 rounded-lg text-left ${
              isDarkMode
                ? "hover:bg-brand-logo-magenta/20 text-brand-logo-magenta"
                : "hover:bg-red-50 text-red-600"
            }`}
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    );
  }

  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="relative group w-10 h-10 rounded-full flex items-center justify-center bg-brand-logo-purple/20 text-brand-logo-purple hover:bg-brand-logo-purple/30 transition-colors border border-brand-logo-purple/30"
          title={userInfo.name}
        >
          {userInfo.image ? (
            <img
              src={userInfo.image}
              alt={userInfo.name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <span className="text-sm font-semibold">{initials}</span>
          )}
          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-900"></div>
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-64' : 'max-h-16'
        }`}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full flex items-center gap-3 p-3 rounded-lg ${
            isDarkMode ? "hover:bg-brand-logo-purple/10" : "hover:bg-base-200"
          } transition-colors relative`}
        >
          <div className="relative w-10 h-10 rounded-full flex items-center justify-center bg-brand-logo-purple/20 text-brand-logo-purple border border-brand-logo-purple/30">
            {userInfo.image ? (
              <img
                src={userInfo.image}
                alt={userInfo.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <span className="text-sm font-semibold">{initials}</span>
            )}
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-brand-void-black"></div>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="font-medium truncate">{userInfo.name}</div>
            <div className="text-xs text-gray-500 truncate">
              {userInfo.email}
            </div>
          </div>
        </button>

        <div 
          className={`px-3 py-2 space-y-1 transition-all duration-300 ease-in-out ${
            isExpanded 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
        >
          <Link
            to={`/${role}/profile`}
            className={`flex items-center gap-2 py-1.5 px-2 rounded text-sm ${
              isDarkMode
                ? "hover:bg-brand-logo-purple/10 text-gray-400 hover:text-brand-logo-purple"
                : "hover:bg-gray-50 text-gray-500 hover:text-gray-800"
            }`}
          >
            <User size={14} />
            <span>My Profile</span>
          </Link>
          
          <button
            onClick={() => handleSignOut(navigate)}
            className={`w-full flex items-center gap-2 py-1.5 px-2 rounded text-left text-sm ${
              isDarkMode
                ? "hover:bg-brand-logo-magenta/20 text-gray-400 hover:text-brand-logo-magenta"
                : "hover:bg-red-50 text-gray-500 hover:text-red-600"
            }`}
          >
            <LogOut size={14} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 